import React, { useCallback, useEffect, useMemo } from "react";

import {
  formatNumericCurrency,
  isFieldEnrolled,
  useCalculateCropMixPotentialPayout,
  useCancelAsyncOnUnmount,
  useGetCarbonProgramOutcomes,
  useGetCropMixComposerSettings,
  useGetFarms,
  useGetFieldDetails,
  useLocation,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { COUNTRY_CODE } from "consts";

import { CMC_MIX_TYPE } from "models";

import { Button, Flex, Icon, Spin, Switch, Tag, Tooltip } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CMC_ROUTES, MAX_SELECTABLE_GOALS } from "pages/app/crop-mix-composer/CropMixComposerOverlay";

type CropMixComposerFooterProps = {
  step: CMC_ROUTES | null;
  onNextClicked: (mixType: CMC_MIX_TYPE | null) => void;
  onApplyToFieldClicked: () => void;
  onBackClicked: () => void;
  fieldId?: string;
};

export const CropMixComposerFooter = ({ step, onNextClicked, onApplyToFieldClicked, onBackClicked, fieldId }: CropMixComposerFooterProps) => {
  const { t } = useTranslation("cropMixComposer");
  const signal = useCancelAsyncOnUnmount()();
  const {
    activeCropMixId,
    isChangingStep,
    isPageLoading,
    mainCropSettings,
    potentialPayout,
    selectedCrops,
    selectedGoals,
    setPotentialPayout,
    autoMixMode,
    setAutoMixMode,
  } = useCropMixComposerContext();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const { user } = useUserContext();
  const countriesToShowCommercialMix = [COUNTRY_CODE.GERMANY, COUNTRY_CODE.AUSTRIA, COUNTRY_CODE.SWITZERLAND, COUNTRY_CODE.ZULU];
  const showCommercialMix = countriesToShowCommercialMix.some((countryCode) => countryCode === user?.farm.country);

  const { currentFarm } = useGetFarms();
  const { data: field } = useGetFieldDetails({ id: fieldId || "" });
  const { data: carbonProgramOutcomes } = useGetCarbonProgramOutcomes({ fieldIds: [fieldId || ""], enabled: !!fieldId });
  const { mutateAsync: calculateCropMixPotentialPayout, isPending: isCalculatingPotentialPayout } = useCalculateCropMixPotentialPayout();
  const { data: settingsCMC } = useGetCropMixComposerSettings();

  const activeSettingsCMC = settingsCMC?.find((setting) => setting.id === activeCropMixId);
  const mixType = activeSettingsCMC?.mixType || null;

  const fieldSize = field?.size || 0;
  const isEnrolled = field ? isFieldEnrolled(carbonProgramOutcomes?.[field.id]) : false;

  const isFieldManagementOverview = fieldId && step === CMC_ROUTES.OVERVIEW;

  const calculatePotentialPayout = useCallback(async () => {
    if (!fieldId) return;

    const { potentialPayout } = await calculateCropMixPotentialPayout({
      fieldId: fieldId,
      cropMixId: activeCropMixId || "",
      isComplexMix: selectedCrops.length > 1,
      signal,
    });

    setPotentialPayout(potentialPayout);
  }, [activeCropMixId, calculateCropMixPotentialPayout, fieldId, selectedCrops, setPotentialPayout, signal]);

  // use the memo to avoid recalculating the payout when the step changes between composition and overview, unless the selected crops or the main crops change
  const shouldCalculatePayout = useMemo(
    () =>
      fieldId &&
      mainCropSettings?.seedingDate &&
      mainCropSettings?.harvestDate &&
      selectedCrops.length > 0 &&
      (step === CMC_ROUTES.COMPOSITION || step === CMC_ROUTES.OVERVIEW),
    [fieldId, mainCropSettings?.seedingDate, mainCropSettings?.harvestDate, selectedCrops.length, step]
  );

  useEffect(() => {
    if (selectedCrops.length === 0) {
      setPotentialPayout(0);
      return;
    }
    if (!shouldCalculatePayout) return;

    calculatePotentialPayout();
    // intentionally including the mainCropSettings object in the dependency array to have the payout recalculated when the main crops change
  }, [shouldCalculatePayout, calculatePotentialPayout, setPotentialPayout, selectedCrops.length, mainCropSettings]);

  return (
    <Flex className="crop-mix-composer-overlay footer" data-testid={`crop-mix-composer-overlay-footer-step-${step}`} align="center" justify="space-between">
      <Flex gap="2rem" align="center">
        {!mixType && step !== CMC_ROUTES.OVERVIEW && step !== CMC_ROUTES.SETTINGS ? (
          <Button
            className="back-button"
            dataTestId="crop-mix-composer-overlay-footer-back-button"
            size="middle"
            type="text"
            onClick={onBackClicked}
            disabled={isChangingStep !== null || isPageLoading}
            loading={isChangingStep === "previous"}
          >
            {t("back")}
          </Button>
        ) : (
          <span />
        )}
      </Flex>

      <Flex gap="2rem" align="center">
        {step === CMC_ROUTES.GOALS && !mixType ? (
          <Flex align="center" gap="0.5rem">
            <p className="margin-0 auto-mix-mode-toggle-text">{t(`page.${CMC_ROUTES.GOALS}.footer.autoMixModeToggle`)}</p>
            <Switch
              className="cmc-auto-mix-mode-toggle"
              data-testid="cmc-auto-mix-mode-toggle"
              checked={autoMixMode}
              disabled={isChangingStep !== null || isPageLoading}
              onChange={() => setAutoMixMode(!autoMixMode)}
            />
            <Tag colour="blue" text={<p className="paragraph-s-bold margin-0">{t(`page.${CMC_ROUTES.GOALS}.footer.betaTag`)}</p>} className="margin-0" />
          </Flex>
        ) : step === CMC_ROUTES.OVERVIEW && showCommercialMix ? (
          <Button
            size="middle"
            type="text"
            disabled={isChangingStep !== null || isPageLoading}
            onClick={() => navigate(`${basePath}cmc/${activeCropMixId}/com-mixes${location.search}`)}
          >
            {t(`page.${CMC_ROUTES.OVERVIEW}.commercialMix`)}
          </Button>
        ) : null}
        {fieldId && isEnrolled && (step === CMC_ROUTES.COMPOSITION || step === CMC_ROUTES.OVERVIEW) && (
          <Flex className="potential-payout" align="center" gap=".25rem">
            <span className="payout-label paragraph-m">{`${t(`page.${CMC_ROUTES.COMPOSITION}.footer.potentialPayout`)}:`}</span>
            <Flex className="amount-icon-container" align="center" gap=".25rem">
              {isCalculatingPotentialPayout ? (
                <Spin size="small" />
              ) : (
                <span className="amount paragraph-m-bold">{`${
                  selectedCrops.length > 0
                    ? formatNumericCurrency((potentialPayout || 0) / fieldSize, {
                        currency: currentFarm?.carbonProgram?.currency,
                        decimals: 0,
                      })
                    : "0 €"
                } / Ha`}</span>
              )}
              <Tooltip title={t(`page.${CMC_ROUTES.COMPOSITION}.footer.potentialPayoutInfo`)}>
                <Icon className="info-icon" iconName="tabler/info-circle" size="s" />
              </Tooltip>
            </Flex>
          </Flex>
        )}
        {step !== CMC_ROUTES.OVERVIEW || isFieldManagementOverview ? (
          <Button
            className={isFieldManagementOverview ? "apply-to-field-button" : "next-button"}
            dataTestId={isFieldManagementOverview ? "crop-mix-composer-overlay-footer-apply-to-field-button" : "crop-mix-composer-overlay-footer-next-button"}
            size="middle"
            type="primary"
            onClick={isFieldManagementOverview ? onApplyToFieldClicked : () => onNextClicked(mixType)}
            disabled={isChangingStep !== null || isPageLoading || (isCalculatingPotentialPayout && step === CMC_ROUTES.OVERVIEW)}
            loading={isChangingStep === "next" || (isCalculatingPotentialPayout && step === CMC_ROUTES.OVERVIEW)}
          >
            {/* we don't show the "continue" button in the overview step of the cmc standalone flow
                if we have a mix type, it means that we are in the edit mode for the current step, so we show the "save changes" button
                if we don't have a mix type, it means that we are in the create mode for the current step, so we show the regular "continue" button text */}
            {step !== CMC_ROUTES.OVERVIEW && mixType
              ? t("saveChanges")
              : t(`page.${step}.footer.continueButton`, { selectedGoalsCount: selectedGoals.length, maxSelectableGoals: MAX_SELECTABLE_GOALS })}
          </Button>
        ) : null}
      </Flex>
    </Flex>
  );
};
